<script setup>
import { reactive, computed, watch, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import { required, minLength } from '@vuelidate/validators';
import { useStore, useMapGetter } from 'dashboard/composables/store';

import Input from 'dashboard/components-next/input/Input.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Editor from 'dashboard/components-next/Editor/Editor.vue';

const props = defineProps({
  assistant: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(['submit']);

const { t } = useI18n();
const toolsStore = useStore();
const availableTools = useMapGetter('captainTools/getRecords');

onMounted(() => {
  toolsStore.dispatch('captainTools/getTools');
});

const initialState = {
  name: '',
  description: '',
  productName: '',
  features: {
    conversationFaqs: false,
    memories: false,
    citations: false,
    contactAttributes: false,
    silentLearning: false,
    sdrMode: false,
    pauseResponse: false,
  },
  enabledTools: {},
};

const state = reactive({ ...initialState });

const validationRules = {
  name: { required, minLength: minLength(1) },
  description: { required, minLength: minLength(1) },
  productName: { required, minLength: minLength(1) },
};

const v$ = useVuelidate(validationRules, state);

const getErrorMessage = field => {
  return v$.value[field].$error ? v$.value[field].$errors[0].$message : '';
};

const formErrors = computed(() => ({
  name: getErrorMessage('name'),
  description: getErrorMessage('description'),
  productName: getErrorMessage('productName'),
}));

const monitoringActive = computed(() => {
  const config = props.assistant?.config || {};
  return config.feature_silent_learning || config.feature_sdr_mode;
});

const isActive = computed(() => {
  return monitoringActive.value;
});

const learningStats = computed(() => {
  return props.assistant?.config?.silent_learning_stats || null;
});

const sdrProfile = computed(() => {
  return props.assistant?.config?.sdr_style_profile || null;
});

const lastActivityFormatted = computed(() => {
  const stats = learningStats.value;
  if (!stats) return 'Nenhuma';
  const timestamps = [
    stats.last_message_observed_at,
    stats.last_audio_transcribed_at,
    stats.last_image_described_at,
    stats.last_faq_generated_at,
    stats.last_sdr_response_at,
  ].filter(Boolean);
  if (!timestamps.length) return 'Nenhuma';
  const latest = new Date(
    Math.max(...timestamps.map(t => new Date(t).getTime()))
  );
  return latest.toLocaleString();
});

const updateStateFromAssistant = assistant => {
  const { config = {} } = assistant;
  state.name = assistant.name;
  state.description = assistant.description;
  state.productName = config.product_name;
  state.features = {
    conversationFaqs: config.feature_faq || false,
    memories: config.feature_memory || false,
    citations: config.feature_citation || false,
    contactAttributes: config.feature_contact_attributes || false,
    silentLearning: config.feature_silent_learning || false,
    sdrMode: config.feature_sdr_mode || false,
    pauseResponse: config.feature_pause_response || false,
  };
  const savedTools = config.enabled_tools || {};
  state.enabledTools = { ...savedTools };
};

const handleBasicInfoUpdate = async () => {
  const result = await Promise.all([
    v$.value.name.$validate(),
    v$.value.description.$validate(),
    v$.value.productName.$validate(),
  ]).then(results => results.every(Boolean));
  if (!result) return;

  const payload = {
    name: state.name,
    description: state.description,
    config: {
      ...props.assistant.config,
      product_name: state.productName,
      feature_faq: state.features.conversationFaqs,
      feature_memory: state.features.memories,
      feature_citation: state.features.citations,
      feature_contact_attributes: state.features.contactAttributes,
      feature_silent_learning: state.features.silentLearning,
      feature_sdr_mode: state.features.sdrMode,
      feature_pause_response: state.features.pauseResponse,
      enabled_tools: { ...state.enabledTools },
    },
  };

  emit('submit', payload);
};

watch(
  () => props.assistant,
  newAssistant => {
    if (newAssistant) updateStateFromAssistant(newAssistant);
  },
  { immediate: true }
);
</script>

<template>
  <div class="flex flex-col gap-6">
    <Input
      v-model="state.name"
      :label="t('CAPTAIN.ASSISTANTS.FORM.NAME.LABEL')"
      :placeholder="t('CAPTAIN.ASSISTANTS.FORM.NAME.PLACEHOLDER')"
      :message="formErrors.name"
      :message-type="formErrors.name ? 'error' : 'info'"
    />

    <Input
      v-model="state.productName"
      :label="t('CAPTAIN.ASSISTANTS.FORM.PRODUCT_NAME.LABEL')"
      :placeholder="t('CAPTAIN.ASSISTANTS.FORM.PRODUCT_NAME.PLACEHOLDER')"
      :message="formErrors.productName"
      :message-type="formErrors.productName ? 'error' : 'info'"
    />

    <Editor
      v-model="state.description"
      :max-length="500"
      :label="t('CAPTAIN.ASSISTANTS.FORM.DESCRIPTION.LABEL')"
      :placeholder="t('CAPTAIN.ASSISTANTS.FORM.DESCRIPTION.PLACEHOLDER')"
      :message="formErrors.description"
      :message-type="formErrors.description ? 'error' : 'info'"
      class="z-0"
    />

    <div class="flex flex-col gap-2">
      <label class="text-sm font-medium text-n-slate-12">
        {{ t('CAPTAIN.ASSISTANTS.FORM.FEATURES.TITLE') }}
      </label>
      <div class="flex flex-col gap-2">
        <label class="flex items-center gap-2">
          <input v-model="state.features.conversationFaqs" type="checkbox" />
          {{ t('CAPTAIN.ASSISTANTS.FORM.FEATURES.ALLOW_CONVERSATION_FAQS') }}
        </label>
        <label class="flex items-center gap-2">
          <input v-model="state.features.memories" type="checkbox" />
          {{ t('CAPTAIN.ASSISTANTS.FORM.FEATURES.ALLOW_MEMORIES') }}
        </label>
        <label class="flex items-center gap-2">
          <input v-model="state.features.citations" type="checkbox" />
          {{ t('CAPTAIN.ASSISTANTS.FORM.FEATURES.ALLOW_CITATIONS') }}
        </label>
        <label class="flex items-center gap-2">
          <input v-model="state.features.contactAttributes" type="checkbox" />
          {{ t('CAPTAIN.ASSISTANTS.FORM.FEATURES.ALLOW_CONTACT_ATTRIBUTES') }}
        </label>
        <label class="flex items-center gap-2">
          <input v-model="state.features.silentLearning" type="checkbox" />
          {{ t('CAPTAIN.ASSISTANTS.FORM.FEATURES.ALLOW_SILENT_LEARNING') }}
        </label>
        <label class="flex items-center gap-2">
          <input v-model="state.features.sdrMode" type="checkbox" />
          {{ t('CAPTAIN.ASSISTANTS.FORM.FEATURES.ALLOW_SDR_MODE') }}
        </label>
        <label
          class="mt-2 flex items-center gap-2 rounded-md border px-3 py-2"
          :class="state.features.pauseResponse
            ? 'border-r-400 bg-r-50 dark:bg-r-900/20 dark:border-r-700'
            : 'border-n-slate-6 bg-n-slate-2'"
        >
          <input v-model="state.features.pauseResponse" type="checkbox" />
          <span :class="state.features.pauseResponse ? 'text-r-700 dark:text-r-300 font-medium' : ''">
            {{ t('CAPTAIN.ASSISTANTS.FORM.FEATURES.PAUSE_RESPONSE') }}
          </span>
        </label>
      </div>
    </div>

    <!-- Silent Learning / SDR Monitoring Panel -->
    <div
      v-if="monitoringActive"
      class="flex flex-col gap-4 rounded-lg border border-n-slate-6 bg-n-slate-2 p-4"
    >
      <div class="flex items-center justify-between">
        <label class="text-sm font-medium text-n-slate-12">
          Monitor de Aprendizado
        </label>
        <span
          class="inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-medium"
          :class="isActive ? 'bg-g-100 text-g-800 dark:bg-g-900 dark:text-g-200' : 'bg-n-slate-3 text-n-slate-11'"
        >
          <span
            class="size-1.5 rounded-full"
            :class="isActive ? 'bg-g-500 animate-pulse' : 'bg-n-slate-8'"
          />
          {{ isActive ? 'Monitorando' : 'Inativo' }}
        </span>
      </div>

      <!-- Stats Grid -->
      <div v-if="learningStats" class="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <div class="flex flex-col rounded-md bg-n-slate-3 p-2.5">
          <span class="text-lg font-semibold text-n-slate-12">{{ learningStats.messages_observed || 0 }}</span>
          <span class="text-xs text-n-slate-10">Mensagens observadas</span>
        </div>
        <div class="flex flex-col rounded-md bg-n-slate-3 p-2.5">
          <span class="text-lg font-semibold text-n-slate-12">{{ learningStats.audio_transcribed || 0 }}</span>
          <span class="text-xs text-n-slate-10">Áudios transcritos</span>
        </div>
        <div class="flex flex-col rounded-md bg-n-slate-3 p-2.5">
          <span class="text-lg font-semibold text-n-slate-12">{{ learningStats.images_described || 0 }}</span>
          <span class="text-xs text-n-slate-10">Imagens analisadas</span>
        </div>
        <div class="flex flex-col rounded-md bg-n-slate-3 p-2.5">
          <span class="text-lg font-semibold text-n-slate-12">{{ learningStats.faqs_generated || 0 }}</span>
          <span class="text-xs text-n-slate-10">FAQs geradas</span>
        </div>
        <div class="flex flex-col rounded-md bg-n-slate-3 p-2.5">
          <span class="text-lg font-semibold text-n-slate-12">{{ learningStats.conversations_learned || 0 }}</span>
          <span class="text-xs text-n-slate-10">Conversas aprendidas</span>
        </div>
        <div class="flex flex-col rounded-md bg-n-slate-3 p-2.5">
          <span class="text-lg font-semibold text-n-slate-12">{{ learningStats.styles_learned || 0 }}</span>
          <span class="text-xs text-n-slate-10">Estilos aprendidos</span>
        </div>
        <div class="flex flex-col rounded-md bg-n-slate-3 p-2.5">
          <span class="text-lg font-semibold text-n-slate-12">{{ learningStats.sdr_responses_sent || 0 }}</span>
          <span class="text-xs text-n-slate-10">Respostas SDR enviadas</span>
        </div>
        <div class="flex flex-col rounded-md bg-n-slate-3 p-2.5">
          <span class="text-xs text-n-slate-10">Última atividade</span>
          <span class="text-xs font-medium text-n-slate-12">{{ lastActivityFormatted }}</span>
        </div>
      </div>

      <!-- SDR Profile Status -->
      <div v-if="sdrProfile" class="flex flex-col gap-2 border-t border-n-slate-6 pt-3">
        <label class="text-xs font-medium text-n-slate-12">
          Perfil SDR Clonado
        </label>
        <div class="grid grid-cols-2 gap-2 text-xs">
          <div>
            <span class="text-n-slate-10">Tom:</span>
            <span class="ms-1 text-n-slate-12">{{ sdrProfile.tone || 'aprendendo...' }}</span>
          </div>
          <div>
            <span class="text-n-slate-10">Saudação:</span>
            <span class="ms-1 text-n-slate-12">{{ sdrProfile.greeting_style || 'aprendendo...' }}</span>
          </div>
          <div>
            <span class="text-n-slate-10">Conversas analisadas:</span>
            <span class="ms-1 text-n-slate-12">{{ sdrProfile.conversations_analyzed || 0 }}</span>
          </div>
          <div>
            <span class="text-n-slate-10">Tamanho resposta:</span>
            <span class="ms-1 text-n-slate-12">{{ sdrProfile.response_length || '-' }}</span>
          </div>
        </div>
        <div v-if="sdrProfile.key_phrases && sdrProfile.key_phrases.length" class="mt-1">
          <span class="text-xs text-n-slate-10">Frases aprendidas:</span>
          <div class="mt-1 flex flex-wrap gap-1">
            <span
              v-for="phrase in sdrProfile.key_phrases.slice(0, 8)"
              :key="phrase"
              class="inline-block rounded-full bg-n-slate-4 px-2 py-0.5 text-xs text-n-slate-11"
            >
              {{ phrase }}
            </span>
          </div>
        </div>
        <div v-if="sdrProfile.sales_techniques && sdrProfile.sales_techniques.length" class="mt-1">
          <span class="text-xs text-n-slate-10">Técnicas de venda:</span>
          <div class="mt-1 flex flex-wrap gap-1">
            <span
              v-for="tech in sdrProfile.sales_techniques.slice(0, 5)"
              :key="tech"
              class="inline-block rounded-full bg-b-50 px-2 py-0.5 text-xs text-b-800 dark:bg-b-900 dark:text-b-200"
            >
              {{ tech }}
            </span>
          </div>
        </div>
      </div>
    </div>

    <div v-if="availableTools.length" class="flex flex-col gap-2">
      <label class="text-sm font-medium text-n-slate-12">
        Ferramentas
      </label>
      <p class="text-xs text-n-slate-11">
        Ative as ferramentas que o assistente pode usar durante as conversas.
      </p>
      <div class="flex flex-col gap-2">
        <label
          v-for="tool in availableTools"
          :key="tool.id"
          class="flex items-center gap-2"
        >
          <input
            v-model="state.enabledTools[tool.id]"
            type="checkbox"
            :true-value="true"
            :false-value="false"
          />
          <span>{{ tool.title }}</span>
          <span class="text-xs text-n-slate-10">{{ tool.description }}</span>
        </label>
      </div>
    </div>

    <div>
      <Button
        :label="t('CAPTAIN.ASSISTANTS.FORM.UPDATE')"
        @click="handleBasicInfoUpdate"
      />
    </div>
  </div>
</template>
